import { Box, Stack, Typography } from '@mui/material'
import { getGridSingleSelectOperators, GridColDef } from '@mui/x-data-grid-premium'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isNull from 'lodash/isNull'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { requestPriorityEnumDataGridComparator } from '@omnea-app/fe-requests/priorityUtils'
import { RequestOwner } from '@omnea-app/fe-requests/RequestOwner'
import { RequestPriorityController } from '@omnea-app/fe-requests/RequestPriorityController'
import { getFormattedIsoDateValueCol } from '@omnea-app/fe-tables/column-formatters/date/getFormattedIsoDateValueCol'
import { getGridAutocompleteFilterOperator } from '@omnea-app/fe-tables/ControlledTable/components/AutocompleteInputFilter'
import { RowGroupingColumnTableCell } from '@omnea-app/fe-tables/row-grouping/components/RowGroupingColumnTableCell'
import { getRowGroupingColumnTableCellValue } from '@omnea-app/fe-tables/row-grouping/helpers'
import {
  applyDefaultColumnDefinitionsToColumns,
  DEFAULT_MINIMUM_DROPDOWN_COLUMN_WIDTH,
} from '@omnea-app/fe-tables/utils/columnDefaults'
import { CounterChip } from '@omnea-app/frontend/components/shared/ui/chip/CounterChip'
import { DefaultChip } from '@omnea-app/frontend/components/shared/ui/chip/DefaultChip'
import { PriceDisplay } from '@omnea-app/frontend/components/shared/ui/price/PriceDisplay'
import { ProfileBox } from '@omnea-app/frontend/components/shared/ui/profile-box/ProfileBox'
import { getCurrencyCodes } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { parseDateString } from '@omnea-app/packages/universal/date-utils/parseDateString'
import { PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { getAnnualPriceValue } from '@omnea-app/packages/universal/price/getAnnualPriceValue'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { TaskStageChip } from '../TaskStageChip'
import { TaskStatusChip } from '../TaskStatusChip'
import { BUYER_LEGAL_ENTITY_COLUMN_NAME } from './getTasksTableDefaultState'
import { TaskRow } from './useTaskTableRows'

interface TaskColumnsConfigs {
  sortable?: boolean
}

const TagChips: React.FC<{ tagNames: string[] }> = ({ tagNames }) => {
  if (isEmpty(tagNames)) {
    return null
  }
  const firstItem = tagNames[0] ?? ''
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <DefaultChip label={firstItem} color="neutral" />
      {tagNames.length > 1 && <CounterChip count={tagNames.length - 1} hint={tagNames.slice(1).join(', ')} />}
    </Stack>
  )
}

export const useTaskColumns = ({ sortable = true }: TaskColumnsConfigs): GridColDef<TaskRow>[] => {
  const { t } = useTranslation('tasks')
  const { user: loggedInUser } = useAuthenticatedUser()
  const { formatDate } = useFormatter()

  const requestProfileColumns: GridColDef<TaskRow>[] = [
    {
      field: BUYER_LEGAL_ENTITY_COLUMN_NAME,
      headerName: t('table-column.buyerLegalEntity'),
      hideable: true,
      editable: false,
      type: 'string',
      display: 'flex',
      valueFormatter: (_, row) => row?.buyerLegalEntity?.name.toString().replaceAll(',', ';'),
      valueGetter: (_, row) => {
        return row?.buyerLegalEntity?.name ?? ''
      },
      renderCell: (params) => {
        if (!params.row.buyerLegalEntity) {
          return null
        }
        return <Typography sx={{ color: 'text.secondary' }}>{params.row.buyerLegalEntity.name}</Typography>
      },
    },
  ]

  const defaultColumns: GridColDef<TaskRow>[] = [
    {
      field: 'name',
      headerName: t('table-column.name'),
      hideable: false,
      sortable,
      groupable: true,
    },
    {
      field: 'publicId',
      headerName: t('table-column.requestId'),
      filterOperators: getGridAutocompleteFilterOperator(),
      hideable: false,
      sortable,
      groupable: true,
    },
    {
      field: 'supplierName',
      sortable,
      headerName: t('table-column.supplierName'),
      filterOperators: getGridAutocompleteFilterOperator('name'),
      valueGetter: (_, row) => row.supplier?.name ?? '',
      renderCell: (params) => {
        const supplier = params.row.supplier
        return (
          <Stack direction="row" spacing={2}>
            <ProfileBox text={supplier?.name ?? ''} imgUrl={supplier?.logoUrl ?? undefined} userId={null} />
          </Stack>
        )
      },
    },
    {
      field: 'type',
      sortable,
      groupable: true,
      display: 'flex',
      headerName: t('table-column.workflow'),
      filterOperators: getGridAutocompleteFilterOperator(),
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          return <RowGroupingColumnTableCell columnHeaderName={t('table-column.workflow')} value={params.value} id={params.id} />
        }
        return params.value
      },
    },
    // NOTE (Dalyan): This should be named `stage` rather than `label`, but renaming would cause the column to be hidden
    // for existings users, and they would have to manually set the column to be visible. We should update the `field` name
    // to `stage` in the future.
    {
      field: 'label',
      sortable,
      groupable: true,
      display: 'flex',
      headerName: t('table-column.stage'),
      type: 'string',
      valueGetter: (_, row) => row.label,
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          return <RowGroupingColumnTableCell columnHeaderName={t('table-column.stage')} value={params.value} id={params.id} />
        }
        const { stage, totalStages, label } = params.row
        return <TaskStageChip stage={stage} totalStages={totalStages} stageLabel={label} />
      },
    },
    {
      field: 'status',
      sortable,
      headerName: t('table-column.status'),
      type: 'string',
      filterOperators: getGridAutocompleteFilterOperator(),
      valueGetter: (_, row) => row.status,
      renderCell: (params) => isDefined(params.row.status) && <TaskStatusChip status={params.row.status} />,
    },
    {
      field: 'allCurrentAssignees',
      sortable,
      headerName: t('table-column.assignees'),
      filterOperators: getGridAutocompleteFilterOperator(),
      valueGetter: (_, row) => row.allCurrentAssignees?.join(', '),
      renderCell: ({ row }) => {
        const assignees = [
          ...(row.currentUserAssignees?.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` })) ?? []),
          ...(row.currentGroupAssignees?.map((g) => ({ id: g.id, name: g.name })) ?? []),
        ]

        const currentUser = assignees.find((i) => i.id === loggedInUser.id)
        const firstItem = currentUser || assignees[0]

        if (!firstItem) return <Stack />

        const otherItems = assignees.filter((i) => i.id !== firstItem.id)
        return (
          <Stack direction="row" spacing={3} alignItems="center" width="100%">
            <Box>
              <ProfileBox text={firstItem.name} avatarVariant="circular" userId={firstItem.id} />
            </Box>
            <CounterChip count={otherItems.length} hint={otherItems.map((i) => i.name).join(', ')} />
          </Stack>
        )
      },
    },
    {
      field: 'createdAt',
      sortable,
      display: 'flex',
      headerName: t('table-column.createdDate'),
      type: 'dateTime',
      valueGetter: (_, row) => parseDateString(row.createdAt),
      valueFormatter: getFormattedIsoDateValueCol,
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          return (
            <RowGroupingColumnTableCell columnHeaderName={t('table-column.createdDate')} value={params.value} id={params.id} />
          )
        }
        if (isNil(params.row.createdAt)) return null
        return (
          <Typography sx={{ color: 'text.secondary' }}>
            {formatDate(new Date(params.row.createdAt), { format: 'short' })}
          </Typography>
        )
      },
    },
    {
      field: 'createdByName',
      sortable,
      headerName: t('table-column.created-by'),
      filterOperators: getGridAutocompleteFilterOperator('name'),
      valueGetter: (_, row) => row.requestor?.name,
      renderCell: (params) => {
        if (isNil(params.value)) return null
        return <ProfileBox text={params.value} avatarVariant="circular" userId={params.row.requestor.id} />
      },
    },
    {
      field: 'owner',
      sortable,
      groupable: true,
      headerName: t('table-column.owner'),
      filterOperators: getGridAutocompleteFilterOperator('name'),
      flex: 1.5,
      minWidth: DEFAULT_MINIMUM_DROPDOWN_COLUMN_WIDTH,
      valueGetter: (_, row) => row.owner?.name,
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          return <RowGroupingColumnTableCell columnHeaderName={t('table-column.owner')} value={params.value} id={params.id} />
        }
        if (isNil(params.row.owner)) return null
        return <RequestOwner requestId={params.row.requestId} owner={params.row.owner} variant="REQUEST_TABLE_COLUMN" />
      },
    },
    {
      field: 'department',
      headerName: t('table-column.department'),
      filterOperators: getGridAutocompleteFilterOperator(),
      hideable: false,
      sortable,
    },
    {
      field: 'currency',
      headerName: t('table-column.currency'),
      filterOperators: getGridSingleSelectOperators(),
      valueOptions: getCurrencyCodes(),
      type: 'singleSelect',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => row.value?.currency,
      renderCell: (params) => {
        return params.row.value?.currency
      },
    },
    {
      field: 'annualValue',
      sortable,
      display: 'flex',
      headerName: t('table-column.annualValue'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => row.value?.amount,
      renderCell: (params) => {
        const value = params.row.value
        if (isNil(value)) return null
        const paymentInterval = value.paymentInterval as PaymentIntervalEnum
        const originalAmount = Number(value.amount)
        const multiYears = value.multiYears ?? null
        const amount = getAnnualPriceValue(originalAmount, paymentInterval, multiYears)
        return (
          <PriceDisplay
            price={{
              ...value,
              paymentInterval,
              amount,
            }}
            hideFrequency
            direction="column"
          />
        )
      },
    },
    {
      field: 'dueOn',
      sortable,
      display: 'flex',
      groupable: true,
      headerName: t('table-column.dueOn'),
      type: 'dateTime',
      valueFormatter: getFormattedIsoDateValueCol,
      valueGetter: (_, row) => parseDateString(row.dueDate),
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          return <RowGroupingColumnTableCell columnHeaderName={t('table-column.dueOn')} value={params.value} id={params.id} />
        }
        if (isNil(params.row.dueDate)) return null
        return (
          <Typography sx={{ color: 'text.secondary' }}>
            {formatDate(new Date(params.row.dueDate), { format: 'short' })}
          </Typography>
        )
      },
    },
    {
      field: 'value',
      sortable,
      headerName: t('table-column.value'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      display: 'flex',
      valueGetter: (_, row) => row.value?.amount,
      renderCell: (params) => {
        const value = params.row.value
        if (isNil(value)) return null
        return (
          <PriceDisplay
            price={{
              ...value,
              paymentInterval: value.paymentInterval as PaymentIntervalEnum,
              amount: Number(value.amount),
            }}
            direction="column"
          />
        )
      },
    },
    {
      field: 'tags',
      headerName: t('table-column.tags'),
      type: 'string',
      filterOperators: getGridAutocompleteFilterOperator('name'),
      valueGetter: (_, row) => (row.tags ?? []).map(({ name }: { name: string }) => name).join(', '),
      renderCell: (params) => {
        const tagNames = params.row.tags?.map((tag) => tag.name) ?? []
        return <TagChips tagNames={tagNames} />
      },
    },
    {
      field: 'autoGeneratedTags',
      headerName: t('table-column.auto-generated-tags'),
      type: 'string',
      filterOperators: getGridAutocompleteFilterOperator(),
      valueGetter: (_, row) => (row.autoGeneratedTags ?? []).map(({ name }: { name: string }) => name).join(', '),
      renderCell: (params) => {
        const tagNames = params.row.autoGeneratedTags?.map((tag) => tag.name) ?? []
        return <TagChips tagNames={tagNames} />
      },
    },
    {
      field: 'priority',
      headerName: t('table-column.priority'),
      sortable,
      groupable: true,
      display: 'flex',
      filterOperators: getGridAutocompleteFilterOperator(),
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          const rowGroupingColumnTableCellValue = getRowGroupingColumnTableCellValue(params.value)
          if (isNull(rowGroupingColumnTableCellValue)) return null
          return (
            <RowGroupingColumnTableCell
              columnHeaderName={t('table-column.priority')}
              value={rowGroupingColumnTableCellValue}
              id={params.id}
            />
          )
        }
        return <RequestPriorityController requestId={params.row.requestId} priority={params.value} displayBorderOnHover />
      },
      sortComparator: requestPriorityEnumDataGridComparator,
    },
  ]

  const columns = defaultColumns.concat(requestProfileColumns)
  return applyDefaultColumnDefinitionsToColumns(columns)
}
